import { useState } from 'react';
import OrganisationTreeNode from 'shared/features/organisation/models/organisation-tree-node';
import SelectedOrganisationTreeNode from 'shared/features/organisation/models/selected-organisation-tree-node';

export type FilterChipOrganizationUnitProps = {
  onOptionsSelected: (keys: SelectedOrganisationTreeNode[]) => void;
  placeHolder: string;
  selectedOptions: SelectedOrganisationTreeNode[];
  locationsOnly?: boolean;
  initialVisibleOrganisationUnitId?: string;
};

type FilterChipOrganizationUnit = {
  isOpen: boolean;
  closeIsOpen: () => void;
  toggleIsOpen: () => void;
  onSelectOrganizationUnit: (selectedNode: OrganisationTreeNode) => void;
  getLabel: () => string;
};

const useFilterChipOrganizationUnit = ({
  onOptionsSelected,
  placeHolder,
  selectedOptions,
}: Readonly<FilterChipOrganizationUnitProps>): FilterChipOrganizationUnit => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleIsOpen = (): void => setIsOpen((prev) => !prev);
  const closeIsOpen = (): void => setIsOpen(false);

  const getAllNodesInSubtree = (node: OrganisationTreeNode): OrganisationTreeNode[] => {
    let nodes: OrganisationTreeNode[] = [node];
    for (const child of node.childOrganisationUnits) {
      nodes = nodes.concat(getAllNodesInSubtree(child));
    }
    return nodes;
  };

  const deselectAffectedNodes = (affectedNodes: OrganisationTreeNode[]): SelectedOrganisationTreeNode[] => {
    const affectedNodeIds = new Set(affectedNodes.map((node) => node.id));
    return selectedOptions.filter((option) => !affectedNodeIds.has(option.id));
  };

  const selectAffectedNodes = (affectedNodes: OrganisationTreeNode[]): SelectedOrganisationTreeNode[] => {
    const nodesToAdd = affectedNodes.map(({ id, name, depth }) => ({ id, name, depth }));

    const existingIds = new Set(selectedOptions.map((option) => option.id));
    const uniqueNodesToAdd = nodesToAdd.filter((node) => !existingIds.has(node.id));

    return [...selectedOptions, ...uniqueNodesToAdd];
  };

  const onSelectOrganizationUnit = (selectedNode: OrganisationTreeNode): void => {
    const affectedNodes = getAllNodesInSubtree(selectedNode);

    const isCurrentlySelected = selectedOptions.some((option) => option.id === selectedNode.id);

    let newSelectedOptions: SelectedOrganisationTreeNode[];

    if (isCurrentlySelected) {
      newSelectedOptions = deselectAffectedNodes(affectedNodes);
    } else {
      newSelectedOptions = selectAffectedNodes(affectedNodes);
    }

    const sortedOptions = newSelectedOptions.toSorted((a, b) => a.depth - b.depth || a.name.localeCompare(b.name));
    onOptionsSelected(sortedOptions);
  };

  const getLabel = (): string => (selectedOptions.length > 0 ? selectedOptions.map((x) => x.name).join(', ') : placeHolder);

  return {
    isOpen,
    closeIsOpen,
    toggleIsOpen,
    onSelectOrganizationUnit,
    getLabel,
  };
};

export default useFilterChipOrganizationUnit;
